import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { PaperClipIcon } from "../../assets/icons/PaperClipIcon";
import { CustomModal, Button, TextInput } from "../shared";
import {
  useCreateSupportDraft,
  useDeleteSupportScreenshot,
  useSubmitSupportSubmission,
  useUploadSupportScreenshot,
} from "../../lib/react-query/support";
import { useProfileDetails } from "../../lib/react-query/profile";
import { getFullName } from "../../utils/utils";

const EMPTY_FORM = {
  submitted_name: "",
  organization_name: "",
  subject: "",
  description: "",
  contact_requested: false,
};

const MAX_SCREENSHOT_BYTES = 10 * 1024 * 1024;
const SUPPORTED_SCREENSHOT_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
]);

const getErrorMessage = (error) => {
  const message = error?.response?.data?.message || error?.message;
  if (Array.isArray(message)) return message.join(" ");
  return message || "We could not submit your request. Please try again.";
};

const readBlobAsFile = (blob) =>
  new File([blob], "dealnest-support-screenshot.png", { type: "image/png" });

const getScreenshotValidationError = (file) => {
  if (!SUPPORTED_SCREENSHOT_TYPES.has(file.type)) {
    return "Choose a JPEG, PNG, GIF, or WebP image.";
  }
  if (file.size < 1) {
    return "The selected image is empty.";
  }
  if (file.size > MAX_SCREENSHOT_BYTES) {
    return "Screenshots must be 10 MB or smaller.";
  }
  return "";
};

const capturePageScreenshot = async () => {
  const target = document.querySelector("[data-support-screenshot-target]");
  if (!target) {
    throw new Error("The DealNest page could not be captured.");
  }

  const { default: html2canvas } = await import("html2canvas");
  const canvas = await html2canvas(target, {
    backgroundColor: "#F8FAFC",
    useCORS: true,
    logging: false,
    scale: Math.min(window.devicePixelRatio || 1, 2),
    ignoreElements: (element) => element.hasAttribute("data-support-modal"),
  });

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) resolve(value);
      else reject(new Error("The screenshot could not be prepared."));
    }, "image/png");
  });
  return readBlobAsFile(blob);
};

export const ContactSupportModal = ({
  open,
  onClose,
  kind = "SUPPORT_REQUEST",
  context = {},
}) => {
  const user = useSelector((state) => state?.user?.user);
  const { data: profileDetails } = useProfileDetails(Boolean(user?.token?.access));
  const [form, setForm] = useState(EMPTY_FORM);
  const [screenshot, setScreenshot] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [submissionId, setSubmissionId] = useState("");
  const [attachmentId, setAttachmentId] = useState(null);
  const [successReference, setSuccessReference] = useState("");
  const [error, setError] = useState("");
  const [captureLoading, setCaptureLoading] = useState(false);
  const screenshotInputRef = useRef(null);

  const createDraft = useCreateSupportDraft();
  const uploadScreenshot = useUploadSupportScreenshot();
  const deleteScreenshot = useDeleteSupportScreenshot();
  const submitSubmission = useSubmitSupportSubmission();

  const initialName = useMemo(
    () => getFullName(profileDetails || user),
    [profileDetails, user],
  );
  const initialOrganization =
    profileDetails?.organization_name ||
    profileDetails?.organization ||
    profileDetails?.company_name ||
    user?.organization_name ||
    "";

  useEffect(() => {
    if (!open) return undefined;
    setForm((previous) => ({
      ...previous,
      submitted_name: previous.submitted_name || initialName,
      organization_name: previous.organization_name || initialOrganization,
    }));
    return undefined;
  }, [initialName, initialOrganization, open]);

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const updateField = (event) => {
    const { name, value, type, checked } = event.target;
    if (name === "description") {
      event.target.style.height = "auto";
      event.target.style.height = `${event.target.scrollHeight}px`;
    }
    setForm((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
    setError("");
  };

  const replaceScreenshot = (file) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setScreenshot(file);
    setPreviewUrl(URL.createObjectURL(file));
    setAttachmentId(null);
  };

  const handleChooseScreenshot = (event) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;

    const validationError = getScreenshotValidationError(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    replaceScreenshot(file);
  };

  const handleCapture = async () => {
    setCaptureLoading(true);
    setError("");
    try {
      const file = await capturePageScreenshot();
      const validationError = getScreenshotValidationError(file);
      if (validationError) throw new Error(validationError);
      replaceScreenshot(file);
    } catch (captureError) {
      setError(captureError?.message || "The page screenshot could not be captured.");
    } finally {
      setCaptureLoading(false);
    }
  };

  const handleRemoveScreenshot = async () => {
    setError("");
    try {
      if (submissionId && attachmentId) {
        await deleteScreenshot.mutateAsync({ submissionId, attachmentId });
      }
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setScreenshot(null);
      setPreviewUrl("");
      setAttachmentId(null);
      if (screenshotInputRef.current) screenshotInputRef.current.value = "";
    } catch (removeError) {
      setError(getErrorMessage(removeError));
    }
  };

  const handleSubmit = async () => {
    const submittedName = form.submitted_name.trim();
    const description = form.description.trim();
    if (!submittedName || !description) {
      setError("Enter your name and describe what happened before submitting.");
      return;
    }

    setError("");
    try {
      let currentSubmissionId = submissionId;
      let currentSubmission;
      if (!currentSubmissionId) {
        currentSubmission = await createDraft.mutateAsync({
          kind,
          severity: "NORMAL",
          subject: form.subject.trim(),
          description,
          submitted_name: submittedName,
          organization_name: form.organization_name.trim(),
          contact_requested: form.contact_requested,
          page_path: context.pagePath || window.location.pathname,
          team_id: context.teamId || undefined,
          project_id: context.projectId || undefined,
          browser_platform: `${navigator.language || "unknown"}; ${navigator.platform || "unknown"}`,
        });
        currentSubmissionId = currentSubmission.public_id;
        setSubmissionId(currentSubmissionId);
      }

      if (screenshot && !attachmentId) {
        currentSubmission = await uploadScreenshot.mutateAsync({
          submissionId: currentSubmissionId,
          file: screenshot,
        });
        const latestAttachment = currentSubmission?.attachments?.find(
          (attachment) => attachment.status === "CONFIRMED",
        );
        setAttachmentId(latestAttachment?.id || null);
      }

      const submitted = await submitSubmission.mutateAsync(currentSubmissionId);
      setSuccessReference(submitted?.reference || "submitted");
    } catch (submitError) {
      setError(getErrorMessage(submitError));
    }
  };

  const handleClose = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setForm(EMPTY_FORM);
    setScreenshot(null);
    setPreviewUrl("");
    setSubmissionId("");
    setAttachmentId(null);
    setSuccessReference("");
    setError("");
    if (screenshotInputRef.current) screenshotInputRef.current.value = "";
    onClose();
  };

  const isSubmitting =
    createDraft.isPending || uploadScreenshot.isPending || submitSubmission.isPending;
  const title = kind === "FEEDBACK" ? "Share feedback" : "Contact DealNest support";

  return (
    <div data-support-modal={open ? "true" : undefined}>
      <CustomModal
        title={title}
        open={open}
        close={handleClose}
        dialogSize="xlarge"
        primaryButtonText={successReference ? "Done" : "Submit request"}
        primaryButtonClick={successReference ? handleClose : handleSubmit}
        primaryButtonLoading={isSubmitting}
        primaryButtonDisabled={captureLoading || deleteScreenshot.isPending}
        secondaryButtonText="Cancel"
        secondaryButtonClick={handleClose}
      >
        {successReference ? (
          <div className="flex flex-col items-center px-4 py-10 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-success/10 text-success">
              <span className="text-2xl" aria-hidden="true">✓</span>
            </div>
            <h3 className="text-lg font-semibold text-primary">Request received</h3>
            <p className="mt-2 max-w-md text-sm leading-6 text-mediumgrey">
              Your reference is <strong className="text-primary">{successReference}</strong>.
              We&apos;ll review it in the Support Inbox. If you asked us to reach out, our team
              will email your account address.
            </p>
          </div>
        ) : (
          <div className="grid items-stretch gap-6 lg:grid-cols-[minmax(0,1fr)_280px]" data-support-modal>
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <TextInput
                  label="Your name"
                  name="submitted_name"
                  value={form.submitted_name}
                  onChange={updateField}
                  required
                  maxLength={200}
                />
                <TextInput
                  label="Organization (optional)"
                  name="organization_name"
                  value={form.organization_name}
                  onChange={updateField}
                  maxLength={255}
                />
              </div>
              <TextInput
                label="Subject (optional)"
                name="subject"
                value={form.subject}
                onChange={updateField}
                maxLength={200}
              />
              <div>
                <label htmlFor="support-description" className="mb-1.5 block text-sm font-medium text-primary">
                  What can we help with?
                </label>
                <textarea
                  id="support-description"
                  name="description"
                  value={form.description}
                  onChange={updateField}
                  maxLength={10000}
                  rows={2}
                  required
                  className="w-full resize-none overflow-hidden rounded-lg border border-surface-border bg-white px-3 py-3 text-sm text-primary outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                  placeholder="Share what is working, what is confusing, or an improvement you would like to see."
                />
                <p className="mt-1 text-right text-xs text-darkgrey">
                  {form.description.length}/10,000
                </p>
                {error ? (
                  <p role="alert" className="mt-2 rounded-lg bg-danger/5 px-3 py-2 text-sm text-danger">
                    {error}
                  </p>
                ) : null}
              </div>
              <div className="flex items-start gap-3 rounded-lg border border-surface-border bg-surface/60 p-3 text-sm text-darkgrey">
                <input
                  type="checkbox"
                  id="support-contact-requested"
                  name="contact_requested"
                  checked={form.contact_requested}
                  onChange={updateField}
                  className="mt-0.5 h-4 w-4 rounded border-surface-border text-primary focus:ring-primary"
                />
                <label htmlFor="support-contact-requested" className="cursor-pointer">
                  <span className="block font-medium text-primary">Please email me about this</span>
                  <span className="mt-0.5 block text-xs text-darkgrey">
                    We only use your account email for follow-up when you opt in.
                  </span>
                </label>
              </div>
            </div>

            <aside className="rounded-xl border border-surface-border bg-surface/70 p-4">
              <p className="text-sm font-semibold text-primary">Optional screenshot</p>
              <p className="mt-1 text-xs leading-5 text-darkgrey">
                Capture just this DealNest page or choose an image from Files/Photos. Page capture
                excludes your browser (such as Chrome) and other apps. JPEG, PNG, GIF, or WebP
                images up to 10 MB are supported. The image is uploaded only when you submit.
              </p>
              {previewUrl ? (
                <div className="mt-4 space-y-3">
                  <img
                    src={previewUrl}
                    alt="Preview of the selected screenshot"
                    className="max-h-44 w-full rounded-lg border border-surface-border object-cover object-top"
                  />
                  <Button
                    variant="outlined"
                    color="danger"
                    size="sm"
                    className="w-full"
                    onClick={handleRemoveScreenshot}
                    loading={deleteScreenshot.isPending}
                  >
                    Remove screenshot
                  </Button>
                </div>
              ) : (
                <div className="mt-3 space-y-1">
                  <Button
                    variant="outlined"
                    color="primary"
                    size="sm"
                    className="w-full"
                    icon={PaperClipIcon}
                    onClick={() => {
                      if (screenshotInputRef.current) {
                        screenshotInputRef.current.value = "";
                        screenshotInputRef.current.click();
                      }
                    }}
                    disabled={captureLoading}
                  >
                    Choose file or photo
                  </Button>
                  <Button
                    variant="transparent"
                    color="primary"
                    size="sm"
                    className="w-full"
                    onClick={handleCapture}
                    loading={captureLoading}
                  >
                    Capture this page instead
                  </Button>
                  <input
                    ref={screenshotInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    aria-label="Choose screenshot file or photo"
                    className="sr-only"
                    onChange={handleChooseScreenshot}
                  />
                </div>
              )}
            </aside>
          </div>
        )}
      </CustomModal>
    </div>
  );
};
